import { getMilestoneEndDate, getMilestoneEndMonth, getMilestoneEndQuarter, getMilestoneEndWeek, getMilestoneStartDate, getMilestoneStartMonth, getMilestoneStartQuarter, getMilestoneStartWeek, getMilestoneStartYear, getMilestoneUpdatedOnMonth, getMilestoneUpdatedOnQuarter, getMilestoneUpdatedOnWeek, getMilestoneUpdatedOnYear, milestoneCreatedOnDate, milestoneUpdatedOnDate, getMilestoneCreatedOnYear, getMilestoneCreatedOnQuarter, getMilestoneCreatedOnMonth, getMilestoneCreatedOnWeek, getMilestoneEndYear } from "../Sort/checkDate/getDate";
import getSimplifiedPlanningSummary from "@/components/Kanban/Workbook/getMilestonePlanningSummary";

const GROUP_BY_KEYS = [
  'actionItemNumber',
  'workstreamActionName',
  'Accountable Workstream',
  'owner',
  'status',
  'selectedForToMeeting',
  'planningSummary',
  'Created on',
  'createdBy',
  'Updated on',
];

const groupMilestonesBy = (data, groupBy, users, initiatives, determineStage, InitiativePlanningSummary, planningSummarySimplified) => {
  if (groupBy === 'None' || groupBy === undefined || GROUP_BY_KEYS.includes(groupBy)) {
    return {};
  }

  const initiativeMilestoneMap = new Map();
  const initiativeHistoryMap = new Map();
  initiatives?.forEach(initiative => {
    initiativeHistoryMap.set(initiative._id, initiative.history || []);
    initiative.milestones?.forEach(milestone => {
      initiativeMilestoneMap.set(milestone._id, initiative);
    });
  });

  const grouped = {};
  data.forEach((milestone) => {
    let groupValue;
    const parentInitiative = initiativeMilestoneMap.get(milestone._id);
    const historyEntries = parentInitiative ? initiativeHistoryMap.get(parentInitiative._id) : [];

    // Correctly determine the main grouping key for the switch statement
    const mainGroupKey = groupBy.includes('_') ? groupBy.split('_')[0] : groupBy;

    switch (mainGroupKey) {
      case 'milestoneNumber':
        groupValue = [milestone.milestoneNumber];
        break;
      case 'name':
        groupValue = [milestone.name];
        break;
      case '# - Name':
        groupValue = [`${milestone.milestoneNumber} - ${milestone.name}`];
        break;
      case 'Owner':
        const owner = users?.find((user) => user._id === milestone.Owner);
        groupValue = [owner ? `${owner.firstName || ''} ${owner.lastName || ''}`.trim() : 'Not Assigned'];
        break;
      case 'Milestone Status':
        groupValue = [milestone.status];
        break;
      case 'type':
        groupValue = [milestone.type];
        break;
      case 'Work starting date':
        const startDate = getMilestoneStartDate(milestone);
        if (!startDate) {
          groupValue = ['<not set>'];
        } else if (groupBy.endsWith('_Day')) {
          groupValue = [startDate.toISOString().split('T')[0]];
        } else if (groupBy.endsWith('_Year')) {
          groupValue = [getMilestoneStartYear(milestone)];
        } else if (groupBy.endsWith('_Quarter')) {
          groupValue = [getMilestoneStartQuarter(milestone)];
        } else if (groupBy.endsWith('_Month')) {
          groupValue = [getMilestoneStartMonth(milestone)];
        } else if (groupBy.endsWith('_Week')) {
          groupValue = [getMilestoneStartWeek(milestone)];
        } else {
          groupValue = [startDate.toISOString().split('T')[0]];
        }
        break;

      case 'Milestone completion date':
        const endDate = getMilestoneEndDate(milestone);
        if (!endDate) {
          groupValue = ['<not set>'];
        } else if (groupBy.endsWith('_Day')) {
          groupValue = [endDate.toISOString().split('T')[0]];
        } else if (groupBy.endsWith('_Year')) {
          groupValue = [getMilestoneEndYear(milestone)];
        } else if (groupBy.endsWith('_Quarter')) {
          groupValue = [getMilestoneEndQuarter(milestone)];
        } else if (groupBy.endsWith('_Month')) {
          groupValue = [getMilestoneEndMonth(milestone)];
        } else if (groupBy.endsWith('_Week')) {
          groupValue = [getMilestoneEndWeek(milestone)];
        } else {
          groupValue = [endDate.toISOString().split('T')[0]];
        }
        break;
      case 'Change in Milestone completion date (binned, current vs last week)':
        const milestoneEndDate = milestone.endDate ? new Date(milestone.endDate) : null;
        const status = milestone.status;
        let binValue = '<not set>';

        if (status === 'Completed') {
          binValue = 'Unchanged';
        } else if (milestoneEndDate) {
          const timeDifference = milestoneEndDate.getTime() - new Date().getTime();
          const daysDifference = Math.ceil(timeDifference / (1000 * 3600 * 24));

          if (daysDifference < 0) {
            binValue = 'Accelerated';
          } else if (daysDifference === 0) {
            binValue = 'Unchanged';
          } else if (daysDifference >= 1 && daysDifference <= 7) {
            binValue = 'Delayed (1 to 7 days)';
          } else if (daysDifference >= 8 && daysDifference <= 14) {
            binValue = 'Delayed (8 to 14 days)';
          } else if (daysDifference >= 15 && daysDifference <= 30) {
            binValue = 'Delayed (15 to 30 days)';
          } else if (daysDifference > 30) {
            binValue = 'Delayed (more than 30 days)';
          }
        }
        groupValue = [binValue];
        break;
      case 'Planning summary (simplified)':
        groupValue = [getSimplifiedPlanningSummary(milestone)];
        break;
      case 'Milestone Planning Summary':
        groupValue = [milestone.planningSummary];
        break;
      case '(LW) Planning summary':
        groupValue = [getMilestoneLastWeekPlanningSummary(milestone)];
        break;
      case 'planningCheck':
        groupValue = [milestone.planningCheck];
        break;
      case 'Created On':
        const createdDate = milestoneCreatedOnDate(milestone, historyEntries);
        if (!createdDate) {
          groupValue = ['<not set>'];
        } else if (groupBy.endsWith('_Day')) {
          groupValue = [createdDate.toISOString().split('T')[0]];
        } else if (groupBy.endsWith('_Year')) {
          groupValue = [getMilestoneCreatedOnYear(milestone, historyEntries)];
        } else if (groupBy.endsWith('_Quarter')) {
          groupValue = [getMilestoneCreatedOnQuarter(milestone, historyEntries)];
        } else if (groupBy.endsWith('_Month')) {
          groupValue = [getMilestoneCreatedOnMonth(milestone, historyEntries)];
        } else if (groupBy.endsWith('_Week')) {
          groupValue = [getMilestoneCreatedOnWeek(milestone, historyEntries)];
        } else {
          groupValue = [createdDate.toISOString().split('T')[0]];
        }
        break;
      case 'Created by':
        const creationHistoryEntry = historyEntries
          .find((entry) => entry.action === `Milestone #${milestone.name} created`);
        const creatorUser = users?.find(u => u._id === creationHistoryEntry?.user);
        groupValue = [creatorUser ? `${creatorUser.firstName || ''} ${creatorUser.lastName || ''}`.trim() : 'N/A'];
        break;
      case 'Updated On':
        const updatedDate = milestoneUpdatedOnDate(milestone, historyEntries);
        if (!updatedDate) {
          groupValue = ['<not set>'];
        } else if (groupBy.endsWith('_Day')) {
          groupValue = [updatedDate.toISOString().split('T')[0]];
        } else if (groupBy.endsWith('_Year')) {
          groupValue = [getMilestoneUpdatedOnYear(milestone, historyEntries)];
        } else if (groupBy.endsWith('_Quarter')) {
          groupValue = [getMilestoneUpdatedOnQuarter(milestone, historyEntries)];
        } else if (groupBy.endsWith('_Month')) {
          groupValue = [getMilestoneUpdatedOnMonth(milestone, historyEntries)];
        } else if (groupBy.endsWith('_Week')) {
          groupValue = [getMilestoneUpdatedOnWeek(milestone, historyEntries)];
        } else {
          groupValue = [updatedDate.toISOString().split('T')[0]];
        }
        break;
      case 'Updated by': {
        const updateHistoryEntries = historyEntries;
        const matchingUpdateEntry = updateHistoryEntries
          .filter(entry => entry.action?.includes(`Milestone with Number ${milestone.milestoneNumber} has been updated.`))
          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];
        const updaterUser = users.find(u => u._id === matchingUpdateEntry?.user);
        groupValue = [updaterUser ? `${updaterUser.firstName || ''} ${updaterUser.lastName || ''}`.trim() : '<not set>'];
        break;
      }
      case 'initiativeName':
        groupValue = [parentInitiative ? parentInitiative.initiativeName : 'Not Associated with an Initiative'];
        break;
      case 'initiativeNumber':
        groupValue = [parentInitiative ? parentInitiative.initiativeNumber : 'Not Associated with an Initiative'];
        break;
      case 'initiativeNumber - initiativeName':
        groupValue = [parentInitiative ? `${parentInitiative.initiativeNumber} - ${parentInitiative.initiativeName}` : 'Not Associated with an Initiative'];
        break;
      case 'stage':
        groupValue = [parentInitiative ? determineStage(parentInitiative) : 'Not Associated with an Initiative'];
        break;
      case 'Stage (simplified)':
        groupValue = [parentInitiative ? parentInitiative.stage : 'Not Associated with an Initiative'];
        break;
      case 'planningSummary':
        groupValue = [parentInitiative ? InitiativePlanningSummary(parentInitiative).planningStatus : 'Not Associated with an Initiative'];
        break;
      case 'Planning summary (simplified)':
        groupValue = [parentInitiative ? planningSummarySimplified(parentInitiative) : 'Not Associated with an Initiative'];
        break;
      case 'workstreamLeadInfo':
      case 'workstreamSponsorInfo':
      case 'initiativeOwnerInfo':
      case 'initiativeOwnerBackupInfo':
      case 'AllowOnlyTo':
        const relatedPeople = parentInitiative?.[groupBy];
        groupValue = relatedPeople && Array.isArray(relatedPeople)
          ? relatedPeople.map((person) => (person?.firstName || person?.lastName ? `${person?.firstName || ''} ${person?.lastName || ''}`.trim() : 'N/A'))
          : ['N/A'];
        break;
      case 'workstreamSponsorWorkstream':
        groupValue = parentInitiative && Array.isArray(parentInitiative.workstreamSponsorWorkstream)
          ? parentInitiative.workstreamSponsorWorkstream.map((ws) => ws?.workstreamName || 'N/A')
          : ['N/A'];
        break;
      case 'workstreamLeadSubworkstream':
        groupValue = parentInitiative && Array.isArray(parentInitiative.workstreamLeadSubworkstream)
          ? parentInitiative.workstreamLeadSubworkstream.map((ws) => ws?.subworkstreamName || 'N/A')
          : ['N/A'];
        break;
      case 'crossFunctionalSupportWorkstream':
        groupValue = parentInitiative && Array.isArray(parentInitiative.crossFunctionalSupportWorkstreamInfo)
          ? parentInitiative.crossFunctionalSupportWorkstreamInfo.map((ws) => ws?.subworkstreamName || 'N/A')
          : ['N/A'];
        break;
      default:
        groupValue = [milestone[groupBy]];
        break;
    }

    // Check if groupValue is an array before attempting to iterate
    if (Array.isArray(groupValue)) {
      groupValue.forEach(val => {
        const finalValue = val === null || val === undefined ? '<not set>' : val;
        if (!grouped[finalValue]) {
          grouped[finalValue] = [];
        }
        grouped[finalValue].push(milestone);
      });
    } else {
      // Fallback for cases where a single string is returned
      const finalValue = groupValue === null || groupValue === undefined ? '<not set>' : groupValue;
      if (!grouped[finalValue]) {
        grouped[finalValue] = [];
      }
      grouped[finalValue].push(milestone);
    }
  });
  return grouped;
};

// const getMilestonePlanningSummary = (milestone) => {
//   if (!milestone) return 'No Status';
//   const { plannedStartDate, plannedEndDate, actualStartDate, actualEndDate } = milestone;

//   if (actualEndDate) {
//     return 'Completed';
//   } else if (actualStartDate) {
//     return 'Started';
//   } else if (plannedEndDate && plannedStartDate) {
//     return 'Planned';
//   } else {
//     return 'No Status';
//   }
// };
const getMilestonePlanningSummarySimplified = (milestone) => {
  if (!milestone) return 'No Status';
  const { plannedStartDate, plannedEndDate } = milestone;

  if (plannedEndDate && plannedStartDate) {
    return 'Planned';
  } else if (plannedEndDate || plannedStartDate) {
    return 'Partially Planned';
  } else {
    return 'No Status';
  }
};
const getMilestoneLastWeekPlanningSummary = (milestone) => {
  if (!milestone) return 'No Status';
  const { previousPlannedStartDate, previousPlannedEndDate, actualStartDate, actualEndDate } = milestone;

  if (actualEndDate) {
    return 'Completed';
  } else if (actualStartDate) {
    return 'Started';
  } else if (previousPlannedEndDate && previousPlannedStartDate) {
    return 'Planned';
  } else {
    return 'No Status';
  }
};

export default groupMilestonesBy;